import {isUndefined} from 'lodash';
import {connect} from 'react-redux';

import {makeGetIsTeamConversation} from '../../../../../../libs/inboxes/store/src/conversations/conversationsSelectors';
import {addQueryToPath} from '../../../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {ConversationTeamStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {fetchAsync} from '../../../../../../libs/shared/core/src/services/http/http';
import {
  InterfaceEventsEnum,
  PopoutLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {makeGetDefaultCellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {CommentUnfurlModel, CommentUnfurlTypesEnum} from '../../../../../models/api/commentUnfurlModel';
import {AppState} from '../../../../../store/appState';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {openDraftLinkedDiscussionAction} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsActions';
import {getAnnotations, getConversations, getFlatActivities} from '../../../../../store/data/dataSelectors';
import {popoutConversationViewerAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {
  makeGetContentPage,
  makeGetViewerPanel,
} from '../../../../../store/panels/viewer/viewerPanelSelectors';
import {CommentViewerConversationUnfurl} from './commentViewerConversationUnfurl';

/*
 * Props.
 */

interface CommentViewerConversationUnfurlContainerProps {
  panelId: ViewerPanelId;
  annotationUid?: string;
  conversationId: number | undefined;
  unfurl: CommentUnfurlModel;
  isInteractionDisabled?: boolean;
}

/*
 * Selectors.
 */

function getUnfurledActivityId(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  if (
    props.unfurl.type !== CommentUnfurlTypesEnum.MESSAGE &&
    props.unfurl.type !== CommentUnfurlTypesEnum.LINKED
  ) {
    return undefined;
  }

  return props.unfurl.activityId;
}

function getAnnotationUid(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  return props.annotationUid;
}

function getUnfurledConversationId(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  return props.unfurl.conversationId;
}

function getUnfurlError(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  return props.unfurl.error;
}

function getUnfurlAccessLink(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  if (props.unfurl.type !== CommentUnfurlTypesEnum.LINKED) {
    return undefined;
  }

  return props.unfurl.accessLink;
}

function getPanelId(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  return props.panelId;
}

function makeGetUnfurledConversation() {
  return createSelector([getConversations, getUnfurledConversationId], (conversations, conversationId) =>
    isUndefined(conversationId) ? undefined : conversations[conversationId],
  );
}

function makeGetAnnotation() {
  return createSelector([getAnnotationUid, getAnnotations], (annotationUid, annotations) =>
    annotationUid ? annotations[annotationUid] : undefined,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CommentViewerConversationUnfurlContainerProps>()(() => {
  const getUnfurledConversation = makeGetUnfurledConversation();
  const getViewerPanel = makeGetViewerPanel(getPanelId);
  const getContentPage = makeGetContentPage(getViewerPanel);

  return {
    annotation: makeGetAnnotation(),
    unfurledConversation: getUnfurledConversation,
    unfurledActivityId: getUnfurledActivityId,
    unfurledAccessLink: getUnfurlAccessLink,
    cellsContext: makeGetDefaultCellsContext(undefined, makeGetIsTeamConversation(getUnfurledConversation)),
    error: getUnfurlError,
    company: getCompany,
    activities: getFlatActivities,
    viewerPage: getContentPage,
  };
});

export const CommentViewerConversationUnfurlContainer = connect(
  mapStateToProps,
  {
    popoutConversationViewer: popoutConversationViewerAction,
    openDraftLinkedDiscussion: openDraftLinkedDiscussionAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const onUnfurlClick: () => void = async () => {
      const {unfurledConversation, unfurledActivityId, unfurledAccessLink} = stateProps;
      if (!unfurledConversation) {
        return;
      }
      if (unfurledAccessLink) {
        const serverUrl = addQueryToPath(`${stateProps.company.url}/app_link`, {link: unfurledAccessLink});
        // Get access to the conversation
        await fetchAsync(serverUrl);
      }

      const isDraftLinkedDiscussion =
        unfurledConversation.messageType === MessageTypesEnum.INTERNAL &&
        unfurledConversation.teamStatus === ConversationTeamStatusesEnum.INVISIBLE;

      if (isDraftLinkedDiscussion) {
        const conversationId = unfurledConversation.id;
        const composeCommentUid = unfurledConversation.draftCommentUids[0];
        if (!composeCommentUid) {
          throw new Error('Should not happen.');
        }
        dispatchProps.openDraftLinkedDiscussion(conversationId, composeCommentUid);
      } else {
        dispatchProps.popoutConversationViewer(
          unfurledConversation.id,
          unfurledActivityId,
          undefined,
          stateProps.viewerPage.parameters,
          PopoutLocationsEnum.COMMENT_UNFURL,
        );
      }

      trackInterfaceEvent(InterfaceEventsEnum.P3_CONVERSATION_PREVIEW_CLICK, {
        conversation_id: ownProps.conversationId,
        unfurled_conversation_id: unfurledConversation?.id,
        unfurled_activity_id: unfurledActivityId,
      });
    };
    return {
      ...ownProps,
      ...stateProps,
      onUnfurlClick,
    };
  },
)(CommentViewerConversationUnfurl);
